import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import {
  ArrowLeft,
  CheckCircle,
  Circle,
  CloudUpload,
  Download,
  FileEarmarkText,
  MusicNoteBeamed,
  PlayFill,
  Floppy,
  Trash
} from 'react-bootstrap-icons';
import EmbedVideoPlayer from '../../common/EmbedVideoPlayer';
import LessonAudioPlayer from '../../common/LessonAudioPlayer';
import { formatAudioDuration } from '../../../helpers/audioLesson';
import {
  enrichLessonDescriptionHtml,
  linkifyLessonDescriptionHtml,
  openLessonDescriptionImageLightbox
} from '../../../helpers/lessonDescriptionMedia';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { resolveEmbedOembedUrl, resolveEmbedPosterSrc } from '../../../helpers/videoEmbed';
import { attachmentId } from './courseTrainingUtils';

const toolbarRowStyle = {
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  gap: '0.5rem',
  minHeight: '2.875rem',
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid #dee2e6',
  background: '#fff',
  boxSizing: 'border-box'
};

function CourseTrainingLessonView({
  isLargeViewport,
  courseId,
  mergedCourse,
  onExitEditor,
  editMode,
  saveCurrentLesson,
  savingLesson,
  lessonIsDirty,
  currentLesson,
  currentModuleId,
  canPlay,
  playUrl,
  isLessonVideoCourseAttachmentStream,
  canPlayAudio,
  audioPlayUrl,
  isLessonAudioCourseAttachmentStream,
  updateLessonField,
  updateLessonAudioUrl,
  lessonQuillWrapRef,
  lessonVideoFileInputRef,
  lessonAudioFileInputRef,
  uploadingLessonVideo,
  uploadingLessonAudio,
  uploadingLessonAttachment = false,
  uploadLessonVideoFile,
  uploadLessonAudioFile,
  clearLessonAudio,
  visibleAttachments,
  videoAttachmentFileId,
  audioAttachmentFileId,
  downloadAttachment,
  removeAttachment,
  addAttachmentFile,
  lessonMarkedComplete,
  togglingLessonComplete = false,
  toggleLessonComplete
}) {
  const attachmentFileInputRef = useRef(null);
  const lessonScrollRef = useRef(null);
  const lessonIdStr = String(currentLesson?._id ?? currentLesson?.id ?? '');
  const [blockLessonPointer, setBlockLessonPointer] = useState(false);
  const [overlayEmbedSrc, setOverlayEmbedSrc] = useState(null);
  const [videoPosters, setVideoPosters] = useState({});

  useEffect(() => {
    if (lessonScrollRef.current) lessonScrollRef.current.scrollTop = 0;
    if (isLargeViewport) {
      setBlockLessonPointer(false);
      return undefined;
    }
    setBlockLessonPointer(true);
    const timer = window.setTimeout(() => setBlockLessonPointer(false), 450);
    return () => window.clearTimeout(timer);
  }, [lessonIdStr, isLargeViewport]);

  useEffect(() => {
    setOverlayEmbedSrc(null);
    setVideoPosters({});
  }, [lessonIdStr]);

  const lessonDescription = currentLesson?.description == null ? '' : String(currentLesson.description);

  useEffect(() => {
    const raw = lessonDescription;
    if (!raw.trim()) return undefined;
    const html = enrichLessonDescriptionHtml(raw);
    const srcs = [];
    const re = /data-lesson-embed="([^"]+)"/g;
    let match;
    while ((match = re.exec(html))) {
      srcs.push(
        match[1]
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
      );
    }
    let cancelled = false;
    srcs.forEach(embed => {
      if (resolveEmbedPosterSrc(embed)) return;
      const oembed = resolveEmbedOembedUrl(embed);
      if (!oembed) return;
      fetch(oembed)
        .then(response => (response.ok ? response.json() : null))
        .then(data => {
          const thumb = data && data.thumbnail_url;
          if (cancelled || !thumb) return;
          setVideoPosters(prev => (prev[embed] === thumb ? prev : { ...prev, [embed]: thumb }));
        })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
    };
  }, [lessonIdStr, lessonDescription]);
  const hasLessonVideo = Boolean(String(currentLesson?.videoUrl || '').trim());
  const hasLessonAudio = Boolean(String(currentLesson?.audio?.url || '').trim());
  const showVideoCard = hasLessonVideo;
  const showAudioPlayer = Boolean(canPlayAudio && audioPlayUrl);
  const showEmptyMediaCard = !showVideoCard && !hasLessonAudio;

  const renderLessonVideoCard = () =>
    showVideoCard ? (
      <Card className="mb-4 border-0 shadow-sm">
        <div className="bg-dark position-relative" style={{ paddingTop: '56.25%' }}>
          {canPlay ? (
            <div
              className="position-absolute"
              style={{ top: 0, left: 0, right: 0, bottom: 0, minWidth: 0, overflow: 'hidden' }}
            >
              <EmbedVideoPlayer
                key={playUrl}
                url={playUrl}
                width="100%"
                height="100%"
                controls
                playsinline
                style={{ width: '100%', height: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </div>
          ) : (
            <div
              className="position-absolute d-flex align-items-center justify-content-center w-100 h-100 flex-column p-4"
              style={{ top: 0, left: 0, color: 'rgba(255,255,255,0.55)' }}
            >
              <PlayFill size={56} className="mb-2" style={{ opacity: 0.5 }} />
              <span className="small">Video player</span>
              {currentLesson.videoUrl ? (
                <span className="small mt-1 text-center" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  {isLessonVideoCourseAttachmentStream ? 'Uploaded course video' : currentLesson.videoUrl}
                </span>
              ) : null}
            </div>
          )}
        </div>
      </Card>
    ) : null;

  return (
    <div
      className="bg-light"
      style={{
        flex: '1 1 0%',
        minWidth: 0,
        minHeight: 0,
        height: isLargeViewport ? '100%' : 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {courseId && mergedCourse && (isLargeViewport || editMode) ? (
        <div style={{ ...toolbarRowStyle, justifyContent: isLargeViewport ? 'space-between' : 'flex-end' }}>
          {isLargeViewport ? (
            <Button variant="outline-secondary" size="sm" onClick={onExitEditor}>
              <ArrowLeft className="mr-1" size={16} />
              Back to courses
            </Button>
          ) : null}
          {editMode ? (
            <Button
              variant="success"
              size="sm"
              onClick={saveCurrentLesson}
              disabled={savingLesson || !currentLesson || !lessonIsDirty}
            >
              {savingLesson ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="mr-2" />
                  Saving…
                </>
              ) : (
                <>
                  <Floppy
                    className="mr-1 flex-shrink-0"
                    size={14}
                    style={{ transform: 'translateY(-1.5px)' }}
                  />
                  Save lesson
                </>
              )}
            </Button>
          ) : null}
        </div>
      ) : null}

      <div
        ref={lessonScrollRef}
        className="course-player-scroll"
        style={{
          flex: '1 1 0%',
          height: 0,
          minHeight: 0,
          minWidth: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          overscrollBehavior: 'none',
          WebkitOverflowScrolling: 'auto',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          pointerEvents: blockLessonPointer ? 'none' : undefined
        }}
      >
        <div className="container py-4" style={{ maxWidth: 960, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
          {!currentLesson ? (
            <Alert variant="light">Select a lesson from the sidebar.</Alert>
          ) : (
            <>
              {renderLessonVideoCard()}

              {showAudioPlayer ? (
                <Card className="mb-4 border-0 shadow-sm">
                  <Card.Body className="p-3 p-md-4">
                    <LessonAudioPlayer
                      key={audioPlayUrl}
                      src={audioPlayUrl}
                      title={currentLesson.audio?.filename || currentLesson.title}
                      durationSeconds={currentLesson.audio?.duration}
                    />
                  </Card.Body>
                </Card>
              ) : null}

              {showEmptyMediaCard ? (
                <Card className="mb-4 border-0 shadow-sm">
                  <div
                    className="bg-dark d-flex align-items-center justify-content-center flex-column p-5"
                    style={{ minHeight: 180, color: 'rgba(255,255,255,0.55)' }}
                  >
                    <PlayFill size={48} className="mb-2" style={{ opacity: 0.45 }} />
                    <span className="small">No lesson video or audio yet</span>
                  </div>
                </Card>
              ) : null}

              {!showVideoCard && hasLessonAudio && !showAudioPlayer ? (
                <Card className="mb-4 border-0 shadow-sm">
                  <Card.Body className="p-4 text-center text-muted">
                    <MusicNoteBeamed size={40} className="mb-2" style={{ opacity: 0.5 }} />
                    <p className="small mb-0">
                      {isLessonAudioCourseAttachmentStream
                        ? 'Uploaded course audio — save the lesson if you just added it.'
                        : 'Audio link saved. Use a direct MP3, WAV, AAC, M4A, or OGG URL to preview here.'}
                    </p>
                  </Card.Body>
                </Card>
              ) : null}

              <Card className="mb-4 shadow-sm">
                <Card.Body>
                  {editMode ? (
                    <>
                      <Form.Group>
                        <Form.Label className="small text-muted">Lesson title *</Form.Label>
                        <Form.Control
                          value={currentLesson.title}
                          onChange={e =>
                            updateLessonField(
                              currentModuleId,
                              String(currentLesson?._id ?? currentLesson?.id ?? ''),
                              'title',
                              e.target.value
                            )
                          }
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className="small text-muted mb-1">Description *</Form.Label>
                        <div ref={lessonQuillWrapRef} className="bg-white lesson-description-editor" style={{ borderRadius: 6, overflow: 'hidden' }}>
                          <ReactQuill
                            key={String(currentLesson?._id ?? currentLesson?.id ?? '')}
                            theme="snow"
                            value={linkifyLessonDescriptionHtml(currentLesson.description || '')}
                            onChange={html =>
                              updateLessonField(
                                currentModuleId,
                                String(currentLesson?._id ?? currentLesson?.id ?? ''),
                                'description',
                                linkifyLessonDescriptionHtml(html)
                              )
                            }
                            modules={{
                              toolbar: [
                                [{ header: [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ list: 'ordered' }, { list: 'bullet' }],
                                ['link'],
                                ['clean']
                              ]
                            }}
                            formats={[
                              'header',
                              'bold',
                              'italic',
                              'underline',
                              'strike',
                              'list',
                              'bullet',
                              'link'
                            ]}
                          />
                        </div>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <div
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '1.2rem',
                            background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
                            padding: '1.125rem 1.25rem 1.25rem',
                            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9)'
                          }}
                        >
                          <h6
                            style={{
                              fontSize: '0.9375rem',
                              fontWeight: 700,
                              color: '#343a40',
                              margin: '0 0 0.35rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            Lesson video
                          </h6>
                          <p
                            className="mb-0"
                            style={{
                              margin: '0 0 1rem',
                              lineHeight: 1.5,
                              color: '#6c757d',
                              fontSize: '0.875rem'
                            }}
                          >
                            Paste a public link, or upload a file.
                          </p>
                          <div className="mt-3">
                            <label
                              style={{
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: '#495057',
                                margin: '0 0 0.4rem',
                                display: 'block'
                              }}
                              htmlFor={`course-lesson-video-url-${String(currentLesson?._id ?? currentLesson?.id ?? '')}`}
                            >
                              Video link
                            </label>
                            <Form.Control
                              id={`course-lesson-video-url-${String(currentLesson?._id ?? currentLesson?.id ?? '')}`}
                              type="url"
                              className="rounded-pill shadow-sm"
                              value={currentLesson.videoUrl || ''}
                              onChange={e =>
                                updateLessonField(
                                  currentModuleId,
                                  String(currentLesson?._id ?? currentLesson?.id ?? ''),
                                  'videoUrl',
                                  e.target.value
                                )
                              }
                              placeholder="https://youtube.com/… or Vimeo, etc."
                            />
                            {courseId && currentLesson.videoUrl && isLessonVideoCourseAttachmentStream ? (
                              <div className="mt-2">
                                <span className="badge badge-pill badge-success font-weight-normal px-3 py-2">
                                  Uploaded video linked
                                </span>
                              </div>
                            ) : null}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              margin: '1.125rem 0',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                              color: '#adb5bd'
                            }}
                            aria-hidden
                          >
                            <div style={{ flex: 1, height: 1, background: '#dee2e6' }} />
                            <span style={{ padding: '0 0.75rem' }}>or</span>
                            <div style={{ flex: 1, height: 1, background: '#dee2e6' }} />
                          </div>
                          <span
                            style={{
                              fontSize: '0.8125rem',
                              fontWeight: 600,
                              color: '#495057',
                              margin: '0 0 0.4rem',
                              display: 'block'
                            }}
                          >
                            Upload from your device
                          </span>
                          <input
                            ref={lessonVideoFileInputRef}
                            id={`course-lesson-video-file-${String(currentLesson?._id ?? currentLesson?.id ?? '')}`}
                            type="file"
                            className="d-none"
                            aria-label="Choose video file to upload"
                            accept="video/*,.mp4,.webm,.mov,.m4v"
                            disabled={uploadingLessonVideo}
                            onChange={e => {
                              const f = e.target.files?.[0];
                              if (f)
                                uploadLessonVideoFile(
                                  currentModuleId,
                                  String(currentLesson?._id ?? currentLesson?.id ?? ''),
                                  f
                                );
                              e.target.value = '';
                            }}
                          />
                          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
                            <Button
                              type="button"
                              variant="outline-primary"
                              className="d-inline-flex align-items-center rounded-pill px-3"
                              disabled={uploadingLessonVideo}
                              onClick={() => lessonVideoFileInputRef.current?.click()}
                            >
                              {uploadingLessonVideo ? (
                                <>
                                  <Spinner animation="border" size="sm" className="mr-2" />
                                  Uploading…
                                </>
                              ) : (
                                <>
                                  <CloudUpload className="mr-2" size={18} />
                                  Choose video file
                                </>
                              )}
                            </Button>
                            <span className="small text-muted">MP4, WebM, MOV…</span>
                          </div>
                        </div>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <div
                          style={{
                            border: '1px solid #dee2e6',
                            borderRadius: '1.2rem',
                            background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
                            padding: '1.125rem 1.25rem 1.25rem',
                            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.9)'
                          }}
                        >
                          <h6
                            style={{
                              fontSize: '0.9375rem',
                              fontWeight: 700,
                              color: '#343a40',
                              margin: '0 0 0.35rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            Lesson audio (Optional)
                          </h6>
                          <p
                            className="mb-0"
                            style={{
                              margin: '0 0 1rem',
                              lineHeight: 1.5,
                              color: '#6c757d',
                              fontSize: '0.875rem'
                            }}
                          >
                            Paste a public link, or upload a file.
                          </p>
                          {hasLessonAudio ? (
                            <div
                              className="mb-3 p-3 rounded"
                              style={{ background: '#fff', border: '1px solid #e9ecef' }}
                            >
                              <div className="d-flex flex-wrap align-items-start justify-content-between" style={{ gap: '0.75rem' }}>
                                <div className="min-w-0">
                                  <span className="d-flex align-items-center small font-weight-bold text-dark mb-1">
                                    <MusicNoteBeamed className="text-primary mr-2 flex-shrink-0" size={18} />
                                    {currentLesson.audio?.filename
                                      ? currentLesson.audio.filename
                                      : isLessonAudioCourseAttachmentStream
                                      ? 'Uploaded course audio'
                                      : 'Audio link'}
                                  </span>
                                  {isLessonAudioCourseAttachmentStream && currentLesson.audio?.size != null ? (
                                    <span className="small text-muted d-block">
                                      {(Number(currentLesson.audio.size) / (1024 * 1024)).toFixed(2)} MB
                                    </span>
                                  ) : null}
                                  {currentLesson.audio?.duration != null &&
                                  Number.isFinite(Number(currentLesson.audio.duration)) ? (
                                    <span className="small text-muted d-block">
                                      Duration: {formatAudioDuration(currentLesson.audio.duration)}
                                    </span>
                                  ) : null}
                                  {!isLessonAudioCourseAttachmentStream && currentLesson.audio?.url ? (
                                    <span
                                      className="small text-muted d-block text-truncate"
                                      style={{ maxWidth: '100%' }}
                                      title={currentLesson.audio.url}
                                    >
                                      {currentLesson.audio.url}
                                    </span>
                                  ) : null}
                                </div>
                                <div className="d-flex flex-shrink-0 flex-wrap" style={{ gap: '0.5rem' }}>
                                  <Button
                                    type="button"
                                    variant="outline-primary"
                                    size="sm"
                                    className="rounded-pill"
                                    disabled={uploadingLessonAudio}
                                    onClick={() => lessonAudioFileInputRef.current?.click()}
                                  >
                                    Replace file
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline-danger"
                                    size="sm"
                                    className="rounded-pill"
                                    disabled={uploadingLessonAudio}
                                    onClick={() =>
                                      clearLessonAudio(currentModuleId, lessonIdStr)
                                    }
                                  >
                                    Remove audio
                                  </Button>
                                </div>
                              </div>
                              {courseId && isLessonAudioCourseAttachmentStream ? (
                                <div className="mt-2">
                                  <span className="badge badge-pill badge-success font-weight-normal px-3 py-2">
                                    Uploaded audio linked
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          <div className="mt-3">
                            <label
                              style={{
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: '#495057',
                                margin: '0 0 0.4rem',
                                display: 'block'
                              }}
                              htmlFor={`course-lesson-audio-url-${lessonIdStr}`}
                            >
                              Audio link
                            </label>
                            <Form.Control
                              id={`course-lesson-audio-url-${lessonIdStr}`}
                              type="url"
                              className="rounded-pill shadow-sm"
                              value={currentLesson.audio?.url || ''}
                              onChange={e =>
                                updateLessonAudioUrl(currentModuleId, lessonIdStr, e.target.value)
                              }
                              placeholder="https://…/lesson.mp3"
                            />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              margin: '1.125rem 0',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.08em',
                              color: '#adb5bd'
                            }}
                            aria-hidden
                          >
                            <div style={{ flex: 1, height: 1, background: '#dee2e6' }} />
                            <span style={{ padding: '0 0.75rem' }}>or</span>
                            <div style={{ flex: 1, height: 1, background: '#dee2e6' }} />
                          </div>
                          <span
                            style={{
                              fontSize: '0.8125rem',
                              fontWeight: 600,
                              color: '#495057',
                              margin: '0 0 0.4rem',
                              display: 'block'
                            }}
                          >
                            Upload from your device
                          </span>
                          <input
                            ref={lessonAudioFileInputRef}
                            id={`course-lesson-audio-file-${lessonIdStr}`}
                            type="file"
                            className="d-none"
                            aria-label="Choose audio file to upload"
                            accept="audio/*,.mp3,.wav,.aac,.m4a,.ogg"
                            disabled={uploadingLessonAudio}
                            onChange={e => {
                              const f = e.target.files?.[0];
                              if (f) uploadLessonAudioFile(currentModuleId, lessonIdStr, f);
                              e.target.value = '';
                            }}
                          />
                          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>
                            <Button
                              type="button"
                              variant="outline-primary"
                              className="d-inline-flex align-items-center rounded-pill px-3"
                              disabled={uploadingLessonAudio}
                              onClick={() => lessonAudioFileInputRef.current?.click()}
                            >
                              {uploadingLessonAudio ? (
                                <>
                                  <Spinner animation="border" size="sm" className="mr-2" />
                                  Uploading…
                                </>
                              ) : (
                                <>
                                  <CloudUpload className="mr-2" size={18} />
                                  {hasLessonAudio ? 'Choose another audio file' : 'Choose audio file'}
                                </>
                              )}
                            </Button>
                            <span className="small text-muted">MP3, WAV, AAC, M4A, OGG…</span>
                          </div>
                        </div>
                      </Form.Group>
                      <Form.Group className="mb-0">
                        <Form.Label className="small text-muted">Platform</Form.Label>
                        <Form.Control
                          value={currentLesson.platform || ''}
                          onChange={e =>
                            updateLessonField(
                              currentModuleId,
                              String(currentLesson?._id ?? currentLesson?.id ?? ''),
                              'platform',
                              e.target.value
                            )
                          }
                          placeholder="YouTube, Vimeo…"
                        />
                      </Form.Group>
                    </>
                  ) : (
                    <>
                      <h4 className="font-weight-bold mb-2">{currentLesson.title}</h4>
                      {(() => {
                        const raw = currentLesson.description == null ? '' : String(currentLesson.description);
                        if (!raw.trim()) {
                          return (
                            <p className="text-muted mb-3 small font-italic">No summary or instructions yet.</p>
                          );
                        }
                        const html = enrichLessonDescriptionHtml(raw, videoPosters);
                        const t = html.trim();
                        const looksHtml = /^<[a-z]/i.test(t) || /<\/(p|div|ul|ol|li|br|img|iframe)\b/i.test(t);
                        if (looksHtml) {
                          return (
                            <div
                              className="mb-3 lesson-description-html"
                              dangerouslySetInnerHTML={{ __html: html }}
                              onClick={e => {
                                const play = e.target.closest('[data-lesson-embed]');
                                const src = play && play.getAttribute('data-lesson-embed');
                                if (src) {
                                  setOverlayEmbedSrc(src);
                                  return;
                                }
                                const img = e.target.closest('img');
                                if (img) openLessonDescriptionImageLightbox(img);
                              }}
                            />
                          );
                        }
                        return (
                          <p className="mb-3 lesson-description-html" style={{ whiteSpace: 'pre-wrap' }}>
                            {raw}
                          </p>
                        );
                      })()}
                    </>
                  )}
                </Card.Body>
              </Card>

              {visibleAttachments.length > 0 || editMode ? (
                <Card className="mb-4 shadow-sm">
                  <Card.Body>
                    <h5 className="font-weight-bold mb-3">Attachments</h5>
                    <p className="small text-muted mb-2">
                      Extra files for this lesson (stored as course uploads). Lesson video and audio uploads are
                      listed only under their sections above.
                    </p>
                    {(currentLesson.attachments || [])
                      .map((attachment, index) => ({ attachment, index }))
                      .filter(({ attachment }) => {
                        const id = attachmentId(attachment);
                        if (videoAttachmentFileId && id === videoAttachmentFileId) return false;
                        if (audioAttachmentFileId && id === audioAttachmentFileId) return false;
                        return true;
                      })
                      .map(({ attachment, index }) => (
                        <div
                          key={`${attachmentId(attachment)}-${index}`}
                          className="d-flex align-items-center justify-content-between p-3 mb-2 rounded bg-light"
                          style={{ gap: 8 }}
                        >
                          <span className="d-flex align-items-center min-w-0 flex-grow-1">
                            <FileEarmarkText className="text-muted mr-2 flex-shrink-0" size={20} />
                            <span className="small text-truncate">
                              {attachment && typeof attachment === 'object' && attachment.filename
                                ? attachment.filename
                                : attachmentId(attachment)}
                            </span>
                          </span>
                          <div className="d-flex align-items-center flex-shrink-0">
                            {!editMode ? (
                              <Button
                                variant="light"
                                size="sm"
                                aria-label="Download attachment"
                                onClick={() => downloadAttachment(attachmentId(attachment))}
                              >
                                <Download size={16} className="text-muted" />
                              </Button>
                            ) : (
                              <Button
                                variant="link"
                                className="p-1 text-danger"
                                title="Remove attachment"
                                onClick={() =>
                                  removeAttachment(
                                    currentModuleId,
                                    String(currentLesson?._id ?? currentLesson?.id ?? ''),
                                    index
                                  )
                                }
                              >
                                <Trash size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    {editMode ? (
                      <div className="mt-2">
                        <Form.Label className="small text-muted">Upload file</Form.Label>
                        <input
                          ref={attachmentFileInputRef}
                          id={`course-lesson-attachment-file-${String(currentLesson?._id ?? currentLesson?.id ?? '')}`}
                          type="file"
                          className="d-none"
                          aria-label="Choose file to attach to this lesson"
                          onChange={e => {
                            const f = e.target.files?.[0];
                            if (f && !uploadingLessonAttachment)
                              addAttachmentFile(
                                currentModuleId,
                                String(currentLesson?._id ?? currentLesson?.id ?? ''),
                                f
                              );
                            e.target.value = '';
                          }}
                        />
                        <div
                          className="d-flex flex-wrap align-items-center"
                          style={{ gap: '0.5rem' }}
                        >
                          <Button
                            type="button"
                            variant="outline-primary"
                            size="sm"
                            className="d-inline-flex align-items-center rounded-pill px-3"
                            disabled={uploadingLessonAttachment}
                            onClick={() => attachmentFileInputRef.current?.click()}
                          >
                            {uploadingLessonAttachment ? (
                              <>
                                <Spinner animation="border" size="sm" className="mr-2" />
                                Uploading…
                              </>
                            ) : (
                              <>
                                <CloudUpload className="mr-1" size={16} />
                                Choose file
                              </>
                            )}
                          </Button>
                          <span className="small text-muted">PDF, images, documents…</span>
                        </div>
                      </div>
                    ) : null}
                  </Card.Body>
                </Card>
              ) : null}

              {!editMode ? (
                <div
                  style={{
                    marginTop: '2rem',
                    marginBottom: '0.5rem',
                    padding: '1.25rem 1.25rem 1.35rem',
                    borderRadius: 12,
                    border: lessonMarkedComplete ? '2px solid #28a745' : '2px solid #dc3545',
                    background: lessonMarkedComplete
                      ? 'linear-gradient(165deg, #d4edda 0%, #ffffff 55%)'
                      : 'linear-gradient(165deg, #f8d7da 0%, #ffffff 52%)',
                    boxShadow: lessonMarkedComplete
                      ? '0 8px 24px rgba(40, 167, 69, 0.18)'
                      : '0 8px 24px rgba(220, 53, 69, 0.2)'
                  }}
                >
                  <p
                    className="mb-0"
                    style={{
                      fontSize: '1.0625rem',
                      fontWeight: 700,
                      marginBottom: '0.875rem',
                      textAlign: 'center',
                      lineHeight: 1.35,
                      color: lessonMarkedComplete ? '#155724' : '#212529'
                    }}
                  >
                    {lessonMarkedComplete
                      ? 'You marked this lesson complete.'
                      : 'Finished this lesson? Mark it complete to track your progress.'}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.875rem' }}>
                    <Button
                      style={{
                        minWidth: '12.75rem',
                        paddingLeft: '1.35rem',
                        paddingRight: '1.35rem',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                        boxShadow: lessonMarkedComplete
                          ? '0 2px 8px rgba(40, 167, 69, 0.2)'
                          : '0 2px 6px rgba(0, 0, 0, 0.12)'
                      }}
                      variant={lessonMarkedComplete ? 'success' : 'secondary'}
                      disabled={togglingLessonComplete}
                      onClick={toggleLessonComplete}
                    >
                      {togglingLessonComplete ? (
                        <>
                          <Spinner animation="border" size="sm" className="mr-2" />
                          Updating…
                        </>
                      ) : lessonMarkedComplete ? (
                        <>
                          <CheckCircle className="mr-2" size={16} />
                          Completed
                        </>
                      ) : (
                        <>
                          <Circle className="mr-2" size={16} />
                          Mark complete
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
      {overlayEmbedSrc ? (
        <div
          role="presentation"
          onClick={() => setOverlayEmbedSrc(null)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 3000,
            background: 'rgba(0, 0, 0, 0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div
            style={{ width: '100%', maxWidth: 960 }}
            onClick={event => event.stopPropagation()}
          >
            <div className="d-flex justify-content-end mb-2">
              <button
                type="button"
                className="btn btn-light btn-sm"
                onClick={() => setOverlayEmbedSrc(null)}
              >
                Close
              </button>
            </div>
            <div className="bg-dark position-relative" style={{ paddingTop: '56.25%' }}>
              <iframe
                title="Lesson video"
                src={overlayEmbedSrc}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: 0
                }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CourseTrainingLessonView;
